import { useParams } from "@reach/router";
import { useProject } from "../../hooks/useProject";
import { useChainlinkDesign } from "../../hooks/useChainlinkDesign";
import { api } from "../../util/api";
import { TextFieldTWSolo } from "../../comp/tw/TextField";
import debounce from "lodash.debounce";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { v4 } from "uuid";
import { useTeam } from "../../hooks/useTeam";
import { MaterialsTable } from "./chainlink/MaterialsTable";
import { GateMaterials } from "./chainlink/GateMaterials";
import { MaterialsSummary } from "./chainlink/MaterialsSummary";
import { ProjectNumbers } from "./chainlink/ProjectNumbers";
import {
  materialKeyMap,
  materialDisplayNames,
} from "./chainlink/materialMappings";
import { calculateAllMaterials } from "./chainlink/calculations";
import { decimalToMixedNumber } from "./chainlink/utils";

const ChainlinkLogic = () => {
  const { projectId } = useParams();
  const { project, isLoading, error, mutate } = useProject(projectId);
  const {
    design,
    isLoading: designIsLoading,
    error: designError,
  } = useChainlinkDesign(project.chainlinkDesignId);
  const { enqueueSnackbar } = useSnackbar();
  const { teamIsLoading, team } = useTeam();

  const [showSimplified, setShowSimplified] = useState(false);
  const timeoutRef = useRef(null);

  const debouncedSaveRef = useRef(
    debounce((projectId, field, value, mutate, enqueueSnackbar) => {
      api("PUT", `/project/${projectId}`, {
        [field]: value,
      })
        .then(() => {
          mutate();
          enqueueSnackbar("Saved", { variant: "success" });
        })
        .catch((error) => {
          console.error("Error updating project:", error);
          enqueueSnackbar("Error saving", { variant: "error" });
          mutate(); // Revert on error
        });
    }, 500),
  ).current;

  if (isLoading || designIsLoading || teamIsLoading)
    return <div className="spinner" />;
  if (error || designError) return <div>Error loading project</div>;

  const allMaterials = calculateAllMaterials(project, design);
  // console.log("All Materials:", allMaterials);

  // Calculate sections/panels for display (similar to traditional fence)
  const lengthsArray = Object.values(project.lengths || []);
  const numberOfSectionsPerLength = lengthsArray.map((length) => {
    // For chainlink, we can consider each post spacing as a "section"
    if (length > design.postSpacing) {
      return Math.ceil(length / design.postSpacing);
    }
    return 1;
  });

  const sizeOfSections = lengthsArray.map((length, idx) => {
    const sections = numberOfSectionsPerLength[idx];
    return (length / sections).toFixed(2);
  });

  const handleGateChange = (field, value) => {
    // Optimistic update
    mutate({ ...project, [field]: value }, false);

    // Debounced backend update
    debouncedSaveRef(projectId, field, value, mutate, enqueueSnackbar);
  };

  console.log("allMaterials", allMaterials);
  return (
    <div className="my-4">
      {/* Cost Adjustment */}
      <div className="mb-4">
        <TextFieldTWSolo
          type="number"
          value={project.adjustCost}
          label="Cost Adjustment Per Ft"
          onChange={(e) => {
            mutate(
              { ...project, adjustCost: e.target.value },
              {
                optimisticData: { ...project, adjustCost: e.target.value },
                revalidate: false,
              },
            );

            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
              api("PUT", `/project/${projectId}`, {
                adjustCost: e.target.value,
              });
            }, 500);
          }}
        />
      </div>

      {/* Gate inputs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <TextFieldTWSolo
          label="Single Gates"
          value={project.chainlinkSingleGates ?? ""}
          onChange={(e) =>
            handleGateChange("chainlinkSingleGates", e.target.value)
          }
          type="number"
        />
        <TextFieldTWSolo
          label="Double Gates"
          value={project.chainlinkDoubleGates ?? ""}
          onChange={(e) =>
            handleGateChange("chainlinkDoubleGates", e.target.value)
          }
          type="number"
        />
        <TextFieldTWSolo
          label="Gate Extensions Required"
          value={project.chainlinkGateExtensions ?? ""}
          onChange={(e) =>
            handleGateChange("chainlinkGateExtensions", e.target.value)
          }
          type="number"
        />
        <TextFieldTWSolo
          label="Number of Gate Posts"
          value={project.chainlinkGatePosts ?? ""}
          onChange={(e) =>
            handleGateChange("chainlinkGatePosts", e.target.value)
          }
          type="number"
        />
      </div>

      {/* Display sections info for each run */}
      {lengthsArray.length > 0 && (
        <div className="mb-4">
          {lengthsArray.map((length, idx) => (
            <div key={v4()} className="grid grid-cols-2 gap-4 mb-2">
              <TextFieldTWSolo
                label={"Number of Sections"}
                value={numberOfSectionsPerLength[idx]}
                readOnly
              />
              <TextFieldTWSolo
                label={"Size Of Sections"}
                value={decimalToMixedNumber(sizeOfSections[idx])}
                readOnly
              />
            </div>
          ))}
        </div>
      )}

      {/* Materials Display */}
      {team.permissions.includes("getMaterials") && allMaterials && (
        <div className="my-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Materials</h2>
            <button
              onClick={() => setShowSimplified(!showSimplified)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm md:text-base"
            >
              {showSimplified ? "Show Details" : "Simplified View"}
            </button>
          </div>

          {/* Table Header */}
          {!showSimplified ? (
            <div className="hidden md:grid grid-cols-5 bg-gray-800 text-white font-semibold text-lg p-3 rounded-t-lg">
              <p>Category</p>
              <p>Material</p>
              <p>Qty</p>
              {team.tierLimits.seePrices && <p>Price</p>}
              <p>Total</p>
            </div>
          ) : (
            <div className="hidden md:grid grid-cols-2 bg-gray-800 text-white font-semibold text-lg p-3 rounded-t-lg">
              <p>Material</p>
              <p>Qty</p>
            </div>
          )}

          <MaterialsTable
            allMaterials={allMaterials}
            materialKeyMap={materialKeyMap}
            materialDisplayNames={materialDisplayNames}
            design={design}
            team={team}
            showSimplified={showSimplified}
          />

          <GateMaterials
            allMaterials={allMaterials}
            design={design}
            team={team}
            showSimplified={showSimplified}
          />

          <MaterialsSummary
            allMaterials={allMaterials}
            materialKeyMap={materialKeyMap}
            design={design}
            team={team}
          />
        </div>
      )}

      <ProjectNumbers
        allMaterials={allMaterials}
        project={project}
        team={team}
        design={design}
        materialKeyMap={materialKeyMap}
      />
    </div>
  );
};

export default ChainlinkLogic;
